package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab
import com.neurobs.ccab.{AccountSubjectResponse, Sex, SubjectCreateData, SubjectIdentityData, SubjectUpdateData}
import com.neurobs.ccabserver.administrator.components.{CreateSubjectDialog, DataPasswordDialog, EditSubjectDialog, RequireDataPassword}
import com.neurobs.ccabserver.administrator.controller.SubjectListController.validateCreateData
import com.neurobs.ccabserver.administrator.view.{CreateSubjectForm, CreateSubjectPanel, SubjectListView}
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelController, AdminPanelDelegate}
import com.neurobs.js.dom.RecordTableView.CommandLinkCell
import com.neurobs.js.view.{Form, Gui, TextBox}
import com.neurobs.js.util.StringUtilities._
import com.neurobs.react.{Button, HtmlAdapter, ModalContainer}
import org.scalajs.dom.raw.HTMLElement
import org.scalajs.dom.raw.Event
import scalatags.JsDom.all._
import slinky.web.ReactDOM
import com.neurobs.ccabserver.administrator.contexts.{GuiContext, LoggerContext, MenuState, RecordCacheContext, ServerContext, UserContext}
import com.neurobs.js.dom.ElementFactory

import scala.concurrent.{Future, Promise}
import scala.util.{Failure, Success, Try}
import slinky.core.annotations.react
import slinky.core.FunctionalComponent
import slinky.core.facade.Hooks.useState

class SubjectListController(server: com.neurobs.ccab.CCABServer, accountId: Long, primaryEmail: String, dataPassword: String, gui: Gui, legacy: Boolean = true) extends AdminListAdapter[AccountSubjectResponse] {

	override def title = if (legacy) s"Account $accountId ($primaryEmail) Subjects" else "Subjects"

	override def itemName = "Subject"

	override def columnHeaders = Seq(
		"Created",
		"Reporting ID",
		"First name",
		"Last name",
		"Birthdate (YYYY-MM-DD)",
		"Birth Year",
		"Sex",
		"Organization ID",
		""
	)

	override def supportClose: Boolean = legacy

	override def itemRow(s: AccountSubjectResponse, delegate: Option[AdminPanelDelegate], controller: AdminListController[AccountSubjectResponse]) = s.identity match {
		case Some(i) =>
			Seq(
				dateString(s.createdAt),
				s.reportingId.noBracketsString + s" (${s.id})",
				i.firstName.getOrElse[String](default = ""),
				i.lastName.getOrElse[String](default = ""),
				i.birthdate.map(_.toString).getOrElse[String](default = ""),
				i.birthYear.toString,
				i.sex.toString,
				i.localID.getOrElse[String](default = ""),
				CommandLinkCell("edit", () => editSubject(s, controller))
			)
		case None =>
			Seq(
				dateString(s.createdAt),
				s.reportingId.noBracketsString + s" (${s.id})",
				"",
				"",
				"",
				"",
				"",
				"",
				CommandLinkCell("edit", () => editSubject(s, controller))
			)
	}

	override val create = Some(createSubject)

	override def get(): Future[Either[String, Seq[AccountSubjectResponse]]] = {
		val promise = Promise[Either[String, Seq[AccountSubjectResponse]]]
		CCABServer.getSubjects(accountId = accountId, dataPassword = dataPassword) {
			case Left(error) => promise.success(Left(error.message))
			case Right(data) => data.error match {
				case Some(error) => promise.success(Left(error))
				case None => promise.success(Right(data.data.get))
			}
		}
		promise.future
	}

	private val dialogDiv = div().render
	override def customContent: Option[HTMLElement] = Some(dialogDiv)

	private def createSubject(c: AdminListController[AccountSubjectResponse]): Unit = {
		dialogDiv.innerHTML = ""
		ReactDOM.render(
			ModalContainer(
				CreateSubjectDialog(
					server = server,
					accountId = accountId,
					dataPassword = dataPassword,
					onFinished = { res =>
						ReactDOM.unmountComponentAtNode(dialogDiv)
						dialogDiv.innerHTML = ""
						if (res.isDefined) c.getList()
					}
				)
			),
			dialogDiv
		)
	}

	private def editSubject(r: AccountSubjectResponse, c: AdminListController[AccountSubjectResponse]): Unit =  {
		dialogDiv.innerHTML = ""
		ReactDOM.render(
			ModalContainer(
				EditSubjectDialog(
					server = server,
					subject = r,
					dataPassword = dataPassword,
					onFinished = { res =>
						ReactDOM.unmountComponentAtNode(dialogDiv)
						dialogDiv.innerHTML = ""
						if (res) c.getList()
					}
				)
			),
			dialogDiv
		)
	}
}

object SubjectListController {
	def apply(server: com.neurobs.ccab.CCABServer, accountId: Long, primaryEmail: String, dataPassword: String, gui: Gui, legacy: Boolean = true) = new AdminListController[AccountSubjectResponse](new SubjectListController(server, accountId, primaryEmail, dataPassword, gui, legacy = legacy))

	def validateCreateData(form: CreateSubjectPanel): Either[String, SubjectIdentityData] = {
		import com.neurobs.js.view.ViewElement._

		def getBirthYear: Either[String, Int] = {
			val thisYear = java.time.LocalDate.now(java.time.ZoneId.of("GMT")).getYear
			Try(form.birthdate.value().toInt) match {
				case Failure(_) => Left("The birth year is not valid.")
				case Success(year) =>
					if ((year > thisYear) || (year < 1915)) Left("The birth year is not valid.")
					else Right(year)
			}
		}
		def getSex: Either[String, Sex.Value] = {
			form.sex.value.flatMap(v => Sex.values.find(_.toString == v)) match {
				case None => Left("The sex selection is invalid.")
				case Some(s) => Right(s)
			}
		}

		if (form.anonymous.checked) {
			for {
				birthYear <- getBirthYear
				sex <- getSex
				localID <- getNonEmpty(form.localId, name = "Local ID")
			} yield SubjectIdentityData(
				firstName = None,
				lastName = None,
				birthdate = None,
				birthYear = birthYear,
				sex = sex,
				localID = Some(localID)
			)
		}
		else {
			for {
				birthdate <- getDate(form.birthdate, name = "birthdate")
				sex <- getSex
				firstName <- getNonEmpty(form.firstName, name = "first name")
				lastName <- getNonEmpty(form.lastName, name = "fast name")
			} yield SubjectIdentityData(
				firstName = Some(firstName),
				lastName = Some(lastName),
				birthdate = Some(birthdate),
				birthYear = birthdate.date.getYear,
				sex = sex,
				localID = getStringOption(form.localId)
			)
		}
	}
}

@react object SubjectListAdapter {
	case class State
	(
		dialogShowing: Boolean,
		controller: Option[AdminPanelController]
	)
	type Props = Unit

	val component = FunctionalComponent[Props] { props =>
		val server = ServerContext.accessState()
		val gui = GuiContext.accessState()
		val user = UserContext.accessState()

		val validUser = user.user.get

		RequireDataPassword { dataPassword =>
			val c = SubjectListController(server = server, accountId = validUser.legacyAccountId, primaryEmail = "", dataPassword = dataPassword, gui = gui, legacy = false)
			HtmlAdapter(element = ElementFactory.toElement(c.container))
		}
	}
}